import { useCallback, useEffect, useRef, useState } from "react";
import { useCurrentRoute } from "../builder/mapping/page";
import { useConfiguration } from "../dataAccess/api/configuration";
import { useEditor } from "../wysiwyg/EditorCtx";
import { removeQueryParams, useQueryParams } from "../dataAccess/QueryParams";
import { CONSENT_SESSION_DURATION } from "../../theme/defaultConstants";
import { useAnalyticsTracker } from "../app/AnalyticsTracker";

class ClientSessionTimer {
  private key: string;

  private durationMsKey: string;

  private expiresAtKey: string;

  constructor(key: string) {
    this.key = key;
    this.durationMsKey = `${this.key}-durationMs`;
    this.expiresAtKey = `${this.key}-expiresAt`;
  }

  storeDuration(durationHours: number) {
    const value = this.validateDuration(durationHours)
      ? durationHours * 3600 * 1000
      : 0;
    sessionStorage.setItem(this.durationMsKey, String(value));
    return value;
  }

  getDuration() {
    const val = sessionStorage.getItem(this.durationMsKey);
    return val !== null ? Number(val) : 0;
  }

  start(): void {
    const expiresAt = Date.now() + this.getDuration();
    sessionStorage.setItem(this.expiresAtKey, String(expiresAt));
  }

  isValid(): boolean {
    const expiresAtStr = sessionStorage.getItem(this.expiresAtKey);
    if (!expiresAtStr) {
      return false;
    }

    const expiresAt = parseInt(expiresAtStr, 10);
    return Date.now() < expiresAt;
  }

  clearExpire(): void {
    sessionStorage.removeItem(this.expiresAtKey);
  }

  clearDuration(): void {
    sessionStorage.removeItem(this.durationMsKey);
  }

  private validateDuration(durationHours: number) {
    return durationHours && typeof durationHours === "number";
  }
}

const consentClientSessionTimer = new ClientSessionTimer("consent-modal");

export function useConsentModalEffect() {
  const { AnalyticsTracker } = useAnalyticsTracker();
  const { configuration } = useConfiguration();
  const { editorParamExist } = useEditor();
  const { currentPage } = useCurrentRoute() || {};
  const { show_consent: showConsent } = useQueryParams() || {};
  const [consentModalOpen, setConsentModalOpen] = useState(
    showConsent !== undefined,
  );
  const { consentModal } =
    configuration?.publishedTemplate?.properties?.settings || {};

  const modalAllowed =
    !currentPage?.isOnboardingPage &&
    !editorParamExist &&
    consentModal &&
    consentModal.addRequiredConsent;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const closeConsentModal = useCallback(() => {
    setConsentModalOpen(false);
  }, []);

  useEffect(() => {
    if (!consentModal?.addRequiredConsent) {
      consentClientSessionTimer.clearDuration();
      consentClientSessionTimer.clearExpire();
    }

    if (!modalAllowed) {
      return;
    }

    const prevDuration = consentClientSessionTimer.getDuration();
    const newDuration = consentClientSessionTimer.storeDuration(
      Number(consentModal.sessionDuration) || CONSENT_SESSION_DURATION,
    );

    if (prevDuration !== newDuration) {
      consentClientSessionTimer.clearExpire();
      AnalyticsTracker.trackIfConsentLoaded();
      setConsentModalOpen(true);
    }
  }, [consentModal, modalAllowed]);

  const acceptConsentModal = useCallback(() => {
    consentClientSessionTimer.start();
    removeQueryParams(["show_consent"]);
    closeConsentModal();
  }, [closeConsentModal]);

  useEffect(() => {
    if (!modalAllowed) {
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    function checkShowModal() {
      if (!consentClientSessionTimer.isValid()) {
        setConsentModalOpen(true);
      }
    }

    checkShowModal();

    intervalRef.current = setInterval(() => checkShowModal(), 60 * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [modalAllowed]);

  return {
    consentModalOpen,
    acceptConsentModal,
  };
}
